import {
  Button,
  healthCriticalIconTileClass,
  healthCriticalSurfaceClass,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import type { LocationDetailDict } from '@/core/components/locations/location-detail-types';
import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupRow,
} from '@/core/components/settings/settings-group';
import { PageHeader } from '@/core/components/page-header';
import type { Dictionary } from '@/core/providers/dictionary-provider';

type LocationDetailPageStatesProps = {
  dict: Dictionary;
  d: LocationDetailDict | undefined;
  breadcrumb: ReactNode;
  onBack: () => void;
};

/** Body-only — route owns the stable `AppIonPage`. */
export function LocationDetailNoWorkspace({
  dict,
  d,
  breadcrumb,
  onBack,
}: LocationDetailPageStatesProps) {
  return (
    <AppPageContent>
      <AppPageLayout>
        <PageHeader
          title={d?.back ?? 'Zurück'}
          breadcrumb={breadcrumb}
          onBack={onBack}
        />
        <div className="flex min-w-0 flex-col gap-2">
          <SettingsGroup>
            <SettingsGroupRow>
              <Button
                variant="outline"
                onClick={onBack}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {d?.back}
              </Button>
            </SettingsGroupRow>
          </SettingsGroup>
          <SettingsGroupFooter>
            {
              (dict as { locations?: { selectWorkspace?: string } }).locations
                ?.selectWorkspace
            }
          </SettingsGroupFooter>
        </div>
      </AppPageLayout>
    </AppPageContent>
  );
}

/** Body-only — route owns the stable `AppIonPage`. */
export function LocationDetailLoading({
  dict,
  breadcrumb,
  onBack,
}: Omit<LocationDetailPageStatesProps, 'd'>) {
  return (
    <AppPageContent>
      <AppPageLayout className="overflow-x-hidden min-w-0 max-w-full md:pb-4">
        <PageHeader
          title={
            (dict as { locations?: { title?: string } }).locations?.title ??
            'Locations'
          }
          breadcrumb={breadcrumb}
          onBack={onBack}
        />
        <div className="flex min-w-0 flex-col gap-4">
          <SettingsGroup>
            <SettingsGroupRow>
              <div className="flex flex-col gap-3">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-32 w-full rounded-[var(--r-control)]" />
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            </SettingsGroupRow>
          </SettingsGroup>
        </div>
      </AppPageLayout>
    </AppPageContent>
  );
}

type LocationDetailMissingProps = LocationDetailPageStatesProps & {
  isError: boolean;
  onRetry: () => void;
};

/** Body-only — route owns the stable `AppIonPage`. */
export function LocationDetailMissing({
  dict,
  d,
  breadcrumb,
  isError,
  onBack,
  onRetry,
}: LocationDetailMissingProps) {
  const loadErrorTitle =
    d?.loadError?.title ?? d?.loadFailed ?? 'Could not load location';
  const loadErrorDescription =
    d?.loadError?.description ??
    'Something went wrong while loading this location. Please try again.';
  const loadErrorRetry = d?.loadError?.retry ?? 'Try again';

  return (
    <AppPageContent>
      <AppPageLayout className="overflow-x-hidden min-w-0 max-w-full md:pb-4">
        <PageHeader
          title={
            (dict as { locations?: { title?: string } }).locations?.title ??
            'Locations'
          }
          breadcrumb={breadcrumb}
          onBack={onBack}
        />
        <div className="flex min-w-0 flex-col gap-2">
          <SettingsGroup
            className={isError ? healthCriticalSurfaceClass : undefined}
          >
            <SettingsGroupRow>
              <div className="flex flex-col items-center justify-center gap-4 py-4 text-center sm:py-6">
                {isError ? (
                  <>
                    <div
                      className={`flex size-16 items-center justify-center rounded-[var(--r-control)] ${healthCriticalIconTileClass}`}
                    >
                      <AlertCircle className="size-8" aria-hidden />
                    </div>
                    <p className="text-[15.5px] font-bold leading-tight text-foreground">
                      {loadErrorTitle}
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button variant="outline" onClick={onRetry}>
                        {loadErrorRetry}
                      </Button>
                      <Button variant="ghost" onClick={onBack}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {d?.back}
                      </Button>
                    </div>
                  </>
                ) : (
                  <Button variant="outline" onClick={onBack}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {d?.back}
                  </Button>
                )}
              </div>
            </SettingsGroupRow>
          </SettingsGroup>
          <SettingsGroupFooter>
            {isError ? loadErrorDescription : d?.notFound}
          </SettingsGroupFooter>
        </div>
      </AppPageLayout>
    </AppPageContent>
  );
}
